import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    retroScrollbarStyles,
    retroBtnClass,
    retroWindowClass,
    retroSunkenClass,
    retroFieldClass,
    retroTabClass,
    formatMailDate,
} from '../../components/staff/retroMail';
import {
    getStaffMail,
    markStaffMailSeen,
    dismissStaffMail,
    getFranklyMailAudience,
    sendFranklyMail,
    uploadStaffImage,
} from '../../services/api';

const KINDS = {
    askfrank: { label: 'AskFrank', from: 'Frank, off his own back' },
    profilereport: { label: 'Report', from: 'The moderation desk' },
    delivery: { label: 'Prize', from: 'The Slime Wheel' },
    note: { label: 'Notice', from: 'Frankly Origins' },
};

const SCOPES = [
    { value: 'person', label: 'One member', blurb: "Lands in that one person's Frankly inbox. Use it for anything personal." },
    { value: 'staff', label: 'All staff', blurb: 'Everyone on the staff team, in their own inbox, where they can keep it.' },
    { value: 'joined', label: 'Members who joined since', blurb: 'Everyone whose account was made on or after the date you pick.' },
    { value: 'everyone', label: 'Everyone', blurb: 'Every member of the site. This is the newsletter option, and it cannot be unsent.' },
    { value: 'staffbox', label: 'The staff mailbox', blurb: 'Posts here rather than a personal inbox, to be dismissed once handled.' },
    { value: 'adminbox', label: 'The admin mailbox', blurb: 'Posts here, admins only.' },
];

function Mailbox({ userData }) {
    const location = useLocation();
    const navigate = useNavigate();
    const isAdmin = Boolean(userData?.hasAdminRole);

    const view = location.pathname.endsWith('/send') && isAdmin ? 'send' : 'open';
    const [state, setState] = useState('open');
    const [messages, setMessages] = useState([]);
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    const [scope, setScope] = useState('person');
    const [habboName, setHabboName] = useState('');
    const [joinedSince, setJoinedSince] = useState('');
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [audience, setAudience] = useState(null);
    const [checking, setChecking] = useState(false);
    const [sending, setSending] = useState(false);
    const [uploading, setUploading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const res = await getStaffMail(state);
        setLoading(false);

        if (res?.success) setMessages(res.messages || []);
        else toast.error(res?.message || 'Could not open the mailbox');
    }, [state]);

    useEffect(() => {
        if (view === 'open') load();
    }, [load, view]);

    useEffect(() => { setSelected(null); }, [state]);

    const checkAudience = useCallback(async () => {
        if (view !== 'send') return;

        setChecking(true);
        const res = await getFranklyMailAudience({ scope, habboName, joinedSince });
        setChecking(false);

        setAudience(res?.success ? res : null);
    }, [view, scope, habboName, joinedSince]);

    useEffect(() => {
        const timer = setTimeout(checkAudience, 350);
        return () => clearTimeout(timer);
    }, [checkAudience]);

    const openMessage = async (message) => {
        setSelected(message);

        if (!message.seen) {
            await markStaffMailSeen(message.id).catch(() => {});
            setMessages(current => current.map(row => (
                row.id === message.id ? { ...row, seen: true } : row
            )));
        }
    };

    const dismiss = async () => {
        if (!selected) return;

        const ok = window.confirm(
            `Mark "${selected.title}" as handled?\n\nIt clears for every staff member, not just you, so only do this once it has actually been dealt with.`
        );
        if (!ok) return;

        setBusy(true);
        const res = await dismissStaffMail(selected.id);
        setBusy(false);

        if (res?.success) {
            toast.success('Marked as handled');
            setSelected(null);
            load();
        } else {
            toast.error(res?.message || 'That did not work');
        }
    };

    const uploadImage = async (file) => {
        setUploading(true);
        const res = await uploadStaffImage(file);
        setUploading(false);

        if (res.success) {
            setImageUrl(res.url);
            toast.success('Image uploaded');
        } else {
            toast.error(res.message || 'That image would not upload');
        }
    };

    const send = async () => {
        if (!title.trim() || !body.trim()) {
            toast.error('It needs a subject and a message');
            return;
        }

        if (!audience || audience.count === 0) {
            toast.error('There is nobody to send that to');
            return;
        }

        const who = audience.mailbox
            ? audience.label
            : `${audience.count} ${audience.count === 1 ? 'member' : 'members'} (${audience.label})`;

        const ok = window.confirm(
            `Send "${title.trim()}" to ${who}?\n\nIt drops into their Frankly inbox straight away and cannot be unsent.`
        );
        if (!ok) return;

        setSending(true);
        const res = await sendFranklyMail({ scope, habboName, joinedSince, title, message: body, imageUrl });
        setSending(false);

        if (res?.success) {
            toast.success(res.message || 'Sent');
            setTitle('');
            setBody('');
            setImageUrl('');
            checkAudience();
        } else {
            toast.error(res?.message || 'That did not send');
        }
    };

    const unseen = messages.filter(message => !message.seen).length;
    const bigSend = !audience?.mailbox && (audience?.count || 0) > 50;

    return (
        <div className="flex w-full flex-col items-center">
            <style>{retroScrollbarStyles}</style>

            <div className="w-full max-w-6xl">
                <div className={retroWindowClass}>
                    <div className="flex select-none items-center bg-maroon px-4 text-sm font-bold text-white">
                        <div className="flex items-center gap-2 py-2">
                            <span>✉️</span>
                            <span>Staff Mailbox - {userData?.habboName || 'staff'}</span>
                        </div>
                    </div>

                    <div className="flex flex-col gap-2 p-2">
                        <div className="relative top-[2px] z-10 flex gap-1 border-b border-white px-1">
                            <button
                                type="button"
                                onClick={() => { navigate('/staff/mailbox'); setState('open'); }}
                                className={retroTabClass(view === 'open' && state === 'open')}
                            >
                                Needs doing {unseen > 0 && state === 'open' ? `(${unseen})` : ''}
                            </button>
                            <button
                                type="button"
                                onClick={() => { navigate('/staff/mailbox'); setState('handled'); }}
                                className={retroTabClass(view === 'open' && state === 'handled')}
                            >
                                Handled
                            </button>
                            {isAdmin && (
                                <button
                                    type="button"
                                    onClick={() => navigate('/staff/mailbox/send')}
                                    className={retroTabClass(view === 'send')}
                                >
                                    Send FranklyMail
                                </button>
                            )}
                        </div>

                        {view === 'open' ? (
                            <div className="flex flex-col border-t-2 border-white">
                                <div className="flex flex-wrap items-center gap-2 bg-[#c0c0c0] px-1 py-2">
                                    <button type="button" onClick={load} className={retroBtnClass}>Refresh</button>
                                    <button
                                        type="button"
                                        onClick={dismiss}
                                        disabled={!selected || selected.handled || busy}
                                        className={retroBtnClass}
                                    >
                                        ✔ Dismiss
                                    </button>
                                    <span className="ml-auto px-1 text-[11px] text-gray-700">
                                        Dismissing clears it for the whole team
                                    </span>
                                </div>

                                <div className={`${retroSunkenClass} h-[250px] overflow-y-scroll scrollbar-retro md:h-[300px]`}>
                                    {loading ? (
                                        <div className="p-10 text-center text-sm italic text-gray-500">Opening the mailbox...</div>
                                    ) : messages.length === 0 ? (
                                        <div className="p-10 text-center text-sm italic text-gray-500">
                                            {state === 'open' ? 'Nothing needs doing. Good.' : 'Nothing has been handled yet.'}
                                        </div>
                                    ) : (
                                        <table className="w-full table-fixed border-collapse text-left text-xs md:text-sm">
                                            <thead className="sticky top-0 z-20 border-b-2 border-[#808080] bg-[#c0c0c0]">
                                                <tr>
                                                    <th className="w-10 border-b border-r border-white p-1 text-center font-normal">!</th>
                                                    <th className="w-24 border-b border-r border-white p-1 font-normal md:w-28">Kind</th>
                                                    <th className="w-28 border-b border-r border-white p-1 font-normal md:w-40">Date</th>
                                                    <th className="border-b border-white p-1 font-normal">Subject</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {messages.map(message => (
                                                    <tr
                                                        key={message.id}
                                                        onClick={() => openMessage(message)}
                                                        className={`cursor-pointer hover:bg-maroon hover:text-white ${
                                                            selected?.id === message.id ? 'bg-maroon text-white' : 'even:bg-[#f9f9f9]'
                                                        } ${!message.seen ? 'font-bold' : ''}`}
                                                    >
                                                        <td className="border-r border-gray-200 p-1 text-center">
                                                            {message.audience === 'admins' ? '🔒' : message.seen ? '' : '●'}
                                                        </td>
                                                        <td className="truncate border-r border-gray-200 p-1">
                                                            {KINDS[message.kind]?.label || message.kind}
                                                        </td>
                                                        <td className="truncate border-r border-gray-200 p-1">
                                                            {formatMailDate(message.sentAt)}
                                                        </td>
                                                        <td className="truncate p-1">{message.title}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>

                                <div className="bg-[#c0c0c0] px-1 py-1 text-[11px] text-gray-700">
                                    {state === 'open' ? 'Needs doing' : 'Handled'}: {messages.length}
                                    &nbsp;·&nbsp; Nobody has opened: {unseen}
                                    {isAdmin && (
                                        <>
                                            &nbsp;·&nbsp;
                                            <strong>
                                                You have Administrator access, so you also see items only an admin can handle.
                                            </strong>
                                        </>
                                    )}
                                </div>

                                <div className="my-0.5 h-2 border-b-2 border-t-2 border-b-white border-t-[#808080] bg-[#c0c0c0]"></div>

                                <div className={`${retroSunkenClass} flex h-[400px] flex-col bg-[#dfdfdf]`}>
                                    {!selected ? (
                                        <div className="flex flex-1 items-center justify-center p-4 text-center italic text-gray-500">
                                            Pick something to read.
                                        </div>
                                    ) : (
                                        <div className="flex h-full flex-col overflow-hidden">
                                            <div className="border-b border-[#808080] bg-[#c0c0c0] p-2 text-xs md:text-sm">
                                                <div className="flex items-start justify-between gap-2">
                                                    <div className="flex min-w-0 flex-col gap-0.5">
                                                        <div>
                                                            <span className="inline-block w-14 text-gray-600">From:</span>{' '}
                                                            <strong>{KINDS[selected.kind]?.from || 'Frankly Origins'}</strong>
                                                        </div>
                                                        <div className="truncate">
                                                            <span className="inline-block w-14 text-gray-600">Subject:</span>{' '}
                                                            <strong className="text-maroon">{selected.title}</strong>
                                                        </div>
                                                    </div>

                                                    <div className="flex shrink-0 gap-2">
                                                        {selected.link && (
                                                            <button
                                                                type="button"
                                                                onClick={() => navigate(selected.link)}
                                                                className={retroBtnClass}
                                                            >
                                                                Open it
                                                            </button>
                                                        )}
                                                        {!selected.handled && (
                                                            <button type="button" onClick={dismiss} disabled={busy} className={retroBtnClass}>
                                                                ✔ Dismiss
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="scrollbar-retro flex-1 overflow-y-scroll bg-white p-4 text-sm leading-relaxed">
                                                <div className="whitespace-pre-wrap break-words">{selected.body}</div>

                                                <hr className="my-3 border-t border-[#c0c0c0]" />

                                                <p className="text-[11px] text-[#808080]">
                                                    Arrived {formatMailDate(selected.sentAt)}
                                                    {selected.handled ? `. Handled ${formatMailDate(selected.handledAt)}.` : '.'}
                                                    {' '}This item was raised automatically by the Frankly Origins system.
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col border-t-2 border-white">
                                <div className="flex flex-wrap items-center gap-2 bg-[#c0c0c0] px-1 py-2">
                                    <button type="button" onClick={send} disabled={sending} className={retroBtnClass}>
                                        {sending ? 'Sending...' : '✉️ Send it'}
                                    </button>
                                    <button type="button" onClick={checkAudience} className={retroBtnClass}>Recount</button>

                                    <span className="ml-auto px-1 text-[11px] text-gray-700">
                                        {checking
                                            ? 'Counting...'
                                            : audience
                                                ? audience.mailbox
                                                    ? `Goes to ${audience.label}`
                                                    : `${audience.count} ${audience.count === 1 ? 'person' : 'people'}: ${audience.label}`
                                                : 'Nobody matched yet'}
                                    </span>
                                </div>

                                <div className="flex flex-col gap-2 md:flex-row">
                                    <div className={`${retroSunkenClass} flex flex-1 flex-col gap-2 bg-[#dfdfdf] p-2`}>
                                        <label className="flex flex-col gap-1 text-xs">
                                            <span className="font-bold uppercase text-gray-600">Who gets it</span>
                                            <select value={scope} onChange={(e) => setScope(e.target.value)} className={retroFieldClass}>
                                                {SCOPES.map(entry => (
                                                    <option key={entry.value} value={entry.value}>{entry.label}</option>
                                                ))}
                                            </select>
                                            <span className="text-[11px] text-gray-600">
                                                {SCOPES.find(entry => entry.value === scope)?.blurb}
                                            </span>
                                        </label>

                                        {scope === 'person' && (
                                            <label className="flex flex-col gap-1 text-xs">
                                                <span className="font-bold uppercase text-gray-600">Habbo name</span>
                                                <input
                                                    type="text"
                                                    value={habboName}
                                                    onChange={(e) => setHabboName(e.target.value)}
                                                    placeholder="Steve"
                                                    className={retroFieldClass}
                                                />
                                            </label>
                                        )}

                                        {scope === 'joined' && (
                                            <label className="flex flex-col gap-1 text-xs">
                                                <span className="font-bold uppercase text-gray-600">Joined on or after</span>
                                                <input
                                                    type="date"
                                                    value={joinedSince}
                                                    onChange={(e) => setJoinedSince(e.target.value)}
                                                    className={retroFieldClass}
                                                />
                                            </label>
                                        )}

                                        <label className="flex flex-col gap-1 text-xs">
                                            <span className="font-bold uppercase text-gray-600">Subject</span>
                                            <input
                                                type="text"
                                                value={title}
                                                onChange={(e) => setTitle(e.target.value)}
                                                maxLength={120}
                                                placeholder="The Frankly Observer, issue 12"
                                                className={retroFieldClass}
                                            />
                                        </label>

                                        <label className="flex flex-1 flex-col gap-1 text-xs">
                                            <span className="font-bold uppercase text-gray-600">Message</span>
                                            <textarea
                                                rows={12}
                                                value={body}
                                                onChange={(e) => setBody(e.target.value)}
                                                className={`${retroFieldClass} scrollbar-retro flex-1`}
                                            />
                                        </label>

                                        <div className="flex flex-col gap-1 text-xs">
                                            <span className="font-bold uppercase text-gray-600">Image</span>

                                            <input
                                                type="text"
                                                value={imageUrl}
                                                onChange={(e) => setImageUrl(e.target.value)}
                                                placeholder="Paste a URL, or upload one below (optional)"
                                                className={retroFieldClass}
                                            />

                                            <div className="flex flex-wrap items-center gap-2">
                                                <label className={retroBtnClass}>
                                                    {uploading ? 'Uploading...' : 'Choose a file...'}
                                                    <input
                                                        type="file"
                                                        accept="image/png,image/jpeg,image/gif,image/webp"
                                                        disabled={uploading}
                                                        onChange={(e) => {
                                                            const file = e.target.files?.[0];
                                                            e.target.value = '';
                                                            if (file) uploadImage(file);
                                                        }}
                                                        className="hidden"
                                                    />
                                                </label>

                                                {imageUrl && (
                                                    <button type="button" onClick={() => setImageUrl('')} className={retroBtnClass}>
                                                        Clear
                                                    </button>
                                                )}

                                                <span className="text-[11px] text-gray-600">
                                                    PNG, JPEG, GIF or WebP, up to 5MB
                                                </span>
                                            </div>
                                        </div>

                                        {bigSend && (
                                            <p className="border-2 border-[#808080] bg-[#ffffcc] p-2 text-[11px] text-black">
                                                <strong>{audience.count} people</strong> will get this in their inbox at once,
                                                and it cannot be taken back. The confirmation will say the number again.
                                            </p>
                                        )}
                                    </div>

                                    <div className={`${retroSunkenClass} flex h-[400px] flex-1 flex-col bg-[#dfdfdf] md:h-auto`}>
                                        <div className="border-b border-[#808080] bg-[#c0c0c0] p-2 text-xs md:text-sm">
                                            <div className="flex flex-col gap-0.5">
                                                <div>
                                                    <span className="inline-block w-14 text-gray-600">From:</span>{' '}
                                                    <strong>Frank &amp; The Frankly Team</strong>
                                                </div>
                                                <div className="truncate">
                                                    <span className="inline-block w-14 text-gray-600">Subject:</span>{' '}
                                                    <strong className="text-maroon">{title || 'No subject yet'}</strong>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="scrollbar-retro flex-1 overflow-y-scroll bg-white p-4 text-sm leading-relaxed">
                                            {imageUrl && (
                                                <img
                                                    src={imageUrl}
                                                    alt="Attachment"
                                                    className="float-right mb-4 ml-4 max-w-[150px] border-2 border-[#808080] bg-[#c0c0c0] p-1"
                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                />
                                            )}

                                            <p className="mb-3">Dear <strong>{audience?.sample?.[0] || 'member'}</strong>,</p>

                                            <div className="whitespace-pre-wrap break-words">
                                                {body || 'Nothing written yet.'}
                                            </div>

                                            <hr className="my-3 border-t border-[#c0c0c0]" />

                                            <p className="text-[11px] text-[#808080]">
                                                This message was automatically generated by the Frankly Origins system.
                                            </p>
                                        </div>

                                        {audience?.sample?.length > 0 && (
                                            <div className="border-t border-[#808080] bg-[#c0c0c0] p-2 text-[11px] text-gray-700">
                                                Going to: {audience.sample.join(', ')}
                                                {audience.count > audience.sample.length
                                                    ? ` and ${audience.count - audience.sample.length} more`
                                                    : ''}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Mailbox;
